import type { AuditRule } from "./types";
import { systemAuditRules } from "./system";
import { loadAuditRuleOrder, loadSystemAuditRuleOverrides, loadUserAuditRules } from "./storage";

/**
 * Load every audit rule, applying:
 *   - system overrides (enabled flag) on top of code-defined system rules
 *   - user-saved rule order on top of [system..., user...] default order
 */
export async function loadAllAuditRules(): Promise<AuditRule[]> {
  const [overrides, userRules, order] = await Promise.all([
    loadSystemAuditRuleOverrides(),
    loadUserAuditRules(),
    loadAuditRuleOrder(),
  ]);

  const systemWithOverrides: AuditRule[] = systemAuditRules.map((r) => {
    const ov = overrides[r.id];
    if (!ov) return r;
    return ov.enabled !== undefined ? { ...r, enabled: ov.enabled } : r;
  });

  const all: AuditRule[] = [...systemWithOverrides, ...userRules];
  if (order.length === 0) return all;

  const byId = new Map(all.map((r) => [r.id, r]));
  const ordered: AuditRule[] = [];
  for (const id of order) {
    const r = byId.get(id);
    if (r) {
      ordered.push(r);
      byId.delete(id);
    }
  }
  // Append any rules not yet in saved order (e.g., new system rules)
  ordered.push(...byId.values());
  return ordered;
}
